import React from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY ANALYSIS VIEW
   ----------------------------------------------------------------------------
   AJUSTEMENTS
   - cartes plus fines
   - padding interne minimisé
   - titres lignes / colonnes sans gras
   - taille titres légèrement augmentée
   - espacements verticaux plus aérés entre header / titres colonnes / matrice
   - drag & drop activé depuis B1 vers B2
   - zone B1 réceptrice pour retour depuis B2
============================================================================ */

/* ============================================================================
   TOKENS UI
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bgSurface: theme?.colors?.background?.surface || "#ffffff",

  quantity: "#0E3F56",
  supply: "#B8621B",
  intensity: "#1A8C74",

  quantityDivider: "rgba(14,63,86,0.20)",
  supplyDivider: "rgba(184,98,27,0.20)",
  intensityDivider: "rgba(26,140,116,0.20)",

  grid: "rgba(14,63,86,0.18)",

  borderNeutral: theme?.colors?.borders?.default || "#d6dee4",
  shadowCard: "0 1px 2px rgba(11,39,53,0.04), 0 3px 8px rgba(11,39,53,0.035)",

  radiusLg: theme?.borders?.radius?.lg || "6px",

  fontXs: "0.68rem",
  fontSm: "0.74rem",
};

/* ============================================================================
   STRUCTURE
============================================================================ */

const trades = [
  { key: "gc", label: "Génie civil", color: "#1E5066" },
  { key: "net", label: "Réseaux", color: "#166A58" },
  { key: "proc", label: "Process", color: "#8A4A13" },
  { key: "elec", label: "Électricité", color: "#3F5D6E" },
  { key: "inst", label: "Instrumentation", color: "#6E1F1F" },
];

const columns = [
  { key: "eqp", label: "Équipements" },
  { key: "mat", label: "Matériaux" },
  { key: "lab", label: "Main d’œuvre" },
];

/* ============================================================================
   CARD
============================================================================ */

const CardMeta = ({ quantity, supply, intensity }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      flexShrink: 0,
    }}
  >
    <span
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.62rem",
        lineHeight: 1,
        color: T.quantity,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {quantity}
    </span>

    <div
      style={{
        width: 1,
        height: 9,
        background: T.quantityDivider,
        flexShrink: 0,
      }}
    />

    <span
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.62rem",
        lineHeight: 1,
        color: T.supply,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {supply}
    </span>

    <div
      style={{
        width: 1,
        height: 9,
        background: T.supplyDivider,
        flexShrink: 0,
      }}
    />

    <span
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.62rem",
        lineHeight: 1,
        color: T.intensity,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {intensity}
    </span>
  </div>
);

const Card = ({ item }) => (
  <div
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData("application/json", JSON.stringify(item));
      e.dataTransfer.setData("source", "B1");
    }}
    style={{
      width: "100%",
      height: 34,
      padding: "0 8px",
      background: T.bgSurface,
      border: `1px solid ${T.borderNeutral}`,
      borderRadius: T.radiusLg,
      boxShadow: T.shadowCard,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      overflow: "hidden",
      boxSizing: "border-box",
      cursor: "grab",
    }}
  >
    <span
      style={{
        minWidth: 0,
        flex: 1,
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.66rem",
        lineHeight: 1,
        color: T.text,
        fontWeight: 400,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {item.title}
    </span>

    <CardMeta
      quantity={item.quantity}
      supply={item.supply}
      intensity={item.intensity}
    />
  </div>
);

/* ============================================================================
   CELL
============================================================================ */

const Cell = ({ items }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 4,
      padding: "6px 8px 0 8px",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    {items.map((it) => (
      <Card key={it.id} item={it} />
    ))}
  </div>
);

/* ============================================================================
   HEADER LEGEND
============================================================================ */

const HeaderLegend = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 18,
      flexShrink: 0,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.72rem",
      lineHeight: 1.2,
      flexWrap: "wrap",
      justifyContent: "flex-end",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: T.quantity, fontWeight: 500 }}>Quantité</span>
    </div>

    <div
      style={{
        width: 1,
        height: 12,
        background: T.quantityDivider,
      }}
    />

    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: T.supply, fontWeight: 500 }}>Approvisionnement</span>
    </div>

    <div
      style={{
        width: 1,
        height: 12,
        background: T.supplyDivider,
      }}
    />

    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: T.intensity, fontWeight: 500 }}>Intensité</span>
    </div>
  </div>
);

/* ============================================================================
   HEADER
============================================================================ */

const HeaderBand = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 12,
      paddingBottom: 22,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.72rem",
        lineHeight: 1.1,
        color: T.verySoft,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}
    >
      Activity analysis
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "1.03rem",
        lineHeight: 1.12,
        color: T.text,
        fontWeight: 600,
      }}
    >
      Lecture opérationnelle du projet
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 18,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.73rem",
          lineHeight: 1.35,
          color: T.verySoft,
          maxWidth: "70%",
        }}
      >
        Projection des lots métier selon la nature d’effort mobilisée : équipements, matériaux et main d’œuvre.
      </div>

      <HeaderLegend />
    </div>
  </div>
);

/* ============================================================================
   LABELS
============================================================================ */

const RowLabel = ({ label, color, rowIndex }) => (
  <div
    style={{
      gridColumn: "1 / 2",
      gridRow: rowIndex + 1,
      padding: "16px 20px 0 2px",
      display: "flex",
      alignItems: "flex-start",
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.corelia || theme?.fonts?.body || "inherit",
        fontSize: "0.86rem",
        lineHeight: 1.18,
        fontWeight: 400,
        color,
        letterSpacing: "0.015em",
      }}
    >
      {label}
    </div>
  </div>
);

const ColumnHeader = ({ label, columnIndex }) => (
  <div
    style={{
      gridColumn: columnIndex + 2,
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-end",
      paddingBottom: 14,
      paddingLeft: 8,
      paddingRight: 8,
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.corelia || theme?.fonts?.body || "inherit",
        fontSize: "0.86rem",
        lineHeight: 1.14,
        fontWeight: 400,
        color: T.soft,
        letterSpacing: "0.025em",
        textAlign: "center",
      }}
    >
      {label}
    </div>
  </div>
);

/* ============================================================================
   MATRIX CELL
============================================================================ */

const MatrixCell = ({
  rowIndex,
  columnIndex,
  items,
  targetTradeKey,
  targetColumnKey,
  setMatrixData,
  setSandboxItems,
}) => {
  const isLastCol = columnIndex === columns.length - 1;
  const isLastRow = rowIndex === trades.length - 1;

  const handleDrop = (e) => {
    e.preventDefault();

    const source = e.dataTransfer.getData("source");
    const raw = e.dataTransfer.getData("application/json");

    if (!raw) return;

    const droppedItem = JSON.parse(raw);

    if (source === "B2") {
      setSandboxItems((prev) => prev.filter((item) => item.id !== droppedItem.id));

      setMatrixData((prev) => {
        const next = { ...prev };
        const currentColumn = { ...(next[targetColumnKey] || {}) };
        const targetItems = [...(currentColumn[targetTradeKey] || [])];

        const exists = targetItems.some((item) => item.id === droppedItem.id);
        if (exists) return prev;

        currentColumn[targetTradeKey] = [
          ...targetItems,
          {
            ...droppedItem,
            columnKey: targetColumnKey,
            tradeKey: targetTradeKey,
            label: droppedItem.title || droppedItem.label,
          },
        ];

        next[targetColumnKey] = currentColumn;
        return next;
      });
    }
  };

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      style={{
        gridColumn: columnIndex + 2,
        gridRow: rowIndex + 1,
        background: "transparent",
        borderRight: !isLastCol ? `1px solid ${T.grid}` : "none",
        borderBottom: !isLastRow ? `1px solid ${T.grid}` : "none",
        boxSizing: "border-box",
      }}
    >
      <Cell items={items} />
    </div>
  );
};

/* ============================================================================
   MATRIX SCROLL AREA
============================================================================ */

const MatrixScrollArea = ({
  matrixData,
  setMatrixData,
  sandboxItems,
  setSandboxItems,
}) => (
  <div
    style={{
      width: "100%",
      height: 560,
      overflowY: "auto",
      overflowX: "hidden",
      boxSizing: "border-box",
      paddingRight: 2,
    }}
  >
    <div
      style={{
        width: "100%",
        minHeight: "100%",
        display: "grid",
        gridTemplateColumns: `160px repeat(${columns.length}, 1fr)`,
        gridTemplateRows: `repeat(${trades.length}, minmax(110px, auto))`,
        background: "transparent",
        boxSizing: "border-box",
      }}
    >
      {trades.map((t, i) => (
        <RowLabel key={t.key} label={t.label} color={t.color} rowIndex={i} />
      ))}

      {trades.map((trade, rowIndex) =>
        columns.map((column, columnIndex) => (
          <MatrixCell
            key={`${trade.key}-${column.key}`}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            targetTradeKey={trade.key}
            targetColumnKey={column.key}
            items={matrixData?.[column.key]?.[trade.key] || []}
            setMatrixData={setMatrixData}
            sandboxItems={sandboxItems}
            setSandboxItems={setSandboxItems}
          />
        ))
      )}
    </div>
  </div>
);

/* ============================================================================
   VIEW
============================================================================ */

const ActivityAnalysisView = ({
  matrixData,
  setMatrixData,
  sandboxItems,
  setSandboxItems,
}) => {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderBand />

      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: `160px repeat(${columns.length}, 1fr)`,
          boxSizing: "border-box",
          marginBottom: 10,
        }}
      >
        <div />

        {columns.map((c, j) => (
          <ColumnHeader key={c.key} label={c.label} columnIndex={j} />
        ))}
      </div>

      <MatrixScrollArea
        matrixData={matrixData}
        setMatrixData={setMatrixData}
        sandboxItems={sandboxItems}
        setSandboxItems={setSandboxItems}
      />
    </div>
  );
};

export default ActivityAnalysisView;